import React from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "../context/LanguageContext";
import { siteConfig } from "../config/siteConfig";
import Logo from "./Logo";
import {
  Phone,
  Mail,
  MapPin,
  MessageCircle,
  Globe2,
} from "lucide-react";

const Footer = () => {
  const { t, lPath } = useLanguage();

  return (
    <footer className="bg-wool-charcoal text-wool-sand border-t border-wool-charcoal/80 pt-16 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-10 pb-12 border-b border-white/10">
          {/* Col 1 & 2: Company Legacy & Mission */}
          <div className="lg:col-span-2 space-y-4">
            <Logo variant="full" light={true} className="h-10" />

            <p className="text-xs text-wool-sand/80 leading-relaxed max-w-md">
              {t("footer.brandStatement")}
            </p>

            <div className="p-3 bg-white/5 border border-white/10 rounded-xl text-xs text-wool-sand/90">
              <span className="font-semibold text-white block mb-1">
                {t("footer.supportingLine")}
              </span>
              <span className="text-[11px] text-wool-sand/70">
                {t("footer.heritage")}
              </span>
            </div>

            {/* Social / Direct Links */}
            <div className="flex flex-wrap items-center gap-3 pt-2">
              {siteConfig.socialLinks.whatsapp_url && (
                <a
                  href={siteConfig.socialLinks.whatsapp_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-700/80 hover:bg-emerald-600 text-white rounded-lg text-xs font-semibold transition"
                >
                  <MessageCircle className="w-3.5 h-3.5" />
                  <span>WhatsApp</span>
                </a>
              )}

              {siteConfig.contacts.googleMapsUrl && (
                <a
                  href={siteConfig.contacts.googleMapsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-white/10 hover:bg-white/20 text-white border border-white/10 rounded-lg text-xs font-semibold transition"
                >
                  <MapPin className="w-3.5 h-3.5 text-wool-sand" />
                  <span>Google Maps</span>
                </a>
              )}

              {siteConfig.socialLinks.instagram_url && (
                <a
                  href={siteConfig.socialLinks.instagram_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-1.5 bg-white/10 hover:bg-white/20 text-white rounded-lg transition text-xs font-semibold"
                >
                  Instagram
                </a>
              )}

              {siteConfig.socialLinks.facebook_url && (
                <a
                  href={siteConfig.socialLinks.facebook_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-1.5 bg-white/10 hover:bg-white/20 text-white rounded-lg transition text-xs font-semibold"
                >
                  Facebook
                </a>
              )}

              {siteConfig.socialLinks.linkedin_url && (
                <a
                  href={siteConfig.socialLinks.linkedin_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-1.5 bg-white/10 hover:bg-white/20 text-white rounded-lg transition text-xs font-semibold"
                >
                  LinkedIn
                </a>
              )}
            </div>
          </div>

          {/* Col 3: Quick Navigation */}
          <div>
            <h4 className="text-xs font-bold text-white uppercase tracking-wider mb-4 border-s-2 border-wool-sand ps-2">
              {t("footer.quickLinks")}
            </h4>
            <ul className="space-y-2.5 text-xs text-wool-sand/80">
              <li>
                <Link to={lPath("/")} className="hover:text-white transition">
                  {t("nav.home")}
                </Link>
              </li>
              <li>
                <Link to={lPath("/about")} className="hover:text-white transition">
                  {t("nav.about")}
                </Link>
              </li>
              <li>
                <Link to={lPath("/wool-specifications")} className="hover:text-white transition">
                  {t("nav.specifications")}
                </Link>
              </li>
              <li>
                <Link to={lPath("/international-sourcing")} className="hover:text-white transition">
                  {t("nav.international")}
                </Link>
              </li>
              <li>
                <Link to={lPath("/for-yarn-manufacturers")} className="hover:text-white transition">
                  {t("nav.yarnManufacturers")}
                </Link>
              </li>
              <li>
                <Link to={lPath("/for-international-suppliers")} className="hover:text-white transition">
                  {t("nav.suppliers")}
                </Link>
              </li>
              <li>
                <Link to={lPath("/partners")} className="hover:text-white transition">
                  {t("nav.partners")}
                </Link>
              </li>
              <li>
                <Link to={lPath("/contact")} className="hover:text-white transition">
                  {t("nav.contact")}
                </Link>
              </li>
            </ul>
          </div>

          {/* Col 4: Sourcing Focus */}
          <div>
            <h4 className="text-xs font-bold text-white uppercase tracking-wider mb-4 border-s-2 border-wool-sand ps-2">
              {t("footer.sourcingMarkets")}
            </h4>
            <ul className="space-y-2.5 text-xs text-wool-sand/80">
              <li className="flex items-center gap-1.5">
                <Globe2 className="w-3.5 h-3.5 text-emerald-400" />
                <span>India (Bikaner & Domestic Hubs)</span>
              </li>
              <li className="flex items-center gap-1.5">
                <Globe2 className="w-3.5 h-3.5 text-wool-sand-dark" />
                <span>Australia (Developing Relationship)</span>
              </li>
              <li className="flex items-center gap-1.5">
                <Globe2 className="w-3.5 h-3.5 text-wool-sand-dark" />
                <span>New Zealand (Developing Relationship)</span>
              </li>
              <li className="flex items-center gap-1.5">
                <Globe2 className="w-3.5 h-3.5 text-wool-sand-dark" />
                <span>China (B2B Supplier Link)</span>
              </li>
              <li className="flex items-center gap-1.5">
                <Globe2 className="w-3.5 h-3.5 text-wool-sand-dark" />
                <span>Syria (Regional Wool Sourcing)</span>
              </li>
              <li className="text-[11px] text-wool-sand/60 pt-1">
                Target: 30+ & 32–34+ Micron Carpet Wool Grades
              </li>
            </ul>
          </div>

          {/* Col 5: Verified Contact Info */}
          <div>
            <h4 className="text-xs font-bold text-white uppercase tracking-wider mb-4 border-s-2 border-wool-sand ps-2">
              Contact Desk
            </h4>
            <div className="space-y-3 text-xs text-wool-sand/80">
              <div className="text-base font-bold text-white font-display">
                S.K. Woollens
              </div>
              <div className="text-[11px] text-wool-sand/65">
                Trusted Wool Supply Since 1965
              </div>
              <div>
                <span className="text-[11px] font-semibold text-white block">Nikhil Banthia (Partner)</span>
                <a href={`tel:${siteConfig.contacts.primaryPhone}`} className="flex items-center gap-1.5 hover:text-white transition mt-0.5">
                  <Phone className="w-3.5 h-3.5 text-emerald-400" />
                  <span>{siteConfig.contacts.primaryPhone}</span>
                </a>
              </div>

              <div>
                <span className="text-[11px] font-semibold text-white block">Sunil Banthia (Partner)</span>
                <a href={`tel:${siteConfig.contacts.secondaryPhone}`} className="flex items-center gap-1.5 hover:text-white transition mt-0.5">
                  <Phone className="w-3.5 h-3.5 text-emerald-400" />
                  <span>{siteConfig.contacts.secondaryPhone}</span>
                </a>
              </div>

              <div>
                <a href={`mailto:${siteConfig.contacts.primaryEmail}`} className="flex items-center gap-1.5 hover:text-white transition">
                  <Mail className="w-3.5 h-3.5 text-wool-sand" />
                  <span>{siteConfig.contacts.primaryEmail}</span>
                </a>
              </div>

              <div className="flex items-start gap-1.5 text-[11px] text-wool-sand/70 pt-1">
                <MapPin className="w-3.5 h-3.5 text-wool-sand shrink-0 mt-0.5" />
                <span>Bikaner, Rajasthan, India</span>
              </div>
            </div>
          </div>
        </div>

        {/* Specialized Sourcing & Supply Landing Hubs Strip */}
        <div className="py-6 border-b border-white/10 text-xs">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
            <span className="text-white font-semibold uppercase tracking-wider text-[11px]">
              Specialized B2B Wool Sourcing Hubs:
            </span>
            <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-wool-sand/80">
              <Link to={lPath("/wool-supplier-bikaner")} className="hover:text-white transition">
                Wool Supplier in Bikaner
              </Link>
              <span className="text-white/20">•</span>
              <Link to={lPath("/carpet-wool-supplier-india")} className="hover:text-white transition">
                Carpet Wool Supplier India
              </Link>
              <span className="text-white/20">•</span>
              <Link to={lPath("/raw-wool-supplier-india")} className="hover:text-white transition">
                Raw Wool Supplier India
              </Link>
              <span className="text-white/20">•</span>
              <Link to={lPath("/wool-agent-bikaner")} className="hover:text-white transition">
                Wool Agent in Bikaner
              </Link>
              <span className="text-white/20">•</span>
              <Link to={lPath("/bulk-wool-supply")} className="hover:text-white transition">
                Bulk Wool Supply
              </Link>
              <span className="text-white/20">•</span>
              <Link to={lPath("/wool-supply-rajasthan")} className="hover:text-white transition">
                Wool Supply in Rajasthan
              </Link>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 flex flex-col sm:flex-row items-center justify-between text-xs text-wool-sand/60 gap-4">
          <div>
            © {new Date().getFullYear()} S.K. Woollens. {t("footer.rights")}
          </div>
          <div className="flex items-center gap-6">
            <Link to={lPath("/privacy-policy")} className="hover:text-white transition">
              {t("footer.privacy")}
            </Link>
            <Link to={lPath("/terms")} className="hover:text-white transition">
              {t("footer.terms")}
            </Link>
            <Link to={lPath("/contact")} className="hover:text-white transition">
              {t("nav.contact")}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
